// MARK://dhk8/pos/components/HelpTip
// Tooltip táctil de ayuda: un ícono (i) que al TAP muestra/oculta una burbuja con
// una explicación corta (y opcionalmente un mini-glosario de términos). Pensado para
// la tablet del POS (hover no aplica), por eso es tap-to-toggle.
//
// La burbuja se renderiza en un PORTAL a document.body con position:fixed y
// coordenadas calculadas desde el trigger (flip arriba/abajo según el espacio,
// clamp horizontal al viewport). Así NUNCA la recorta el overflow de un modal o
// contenedor con scroll — y admite contenido alto (glosario). El trigger es un
// <span role="button"> (NO <button>) para poder anidarlo dentro de otro botón.
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'

export interface HelpDef { term: string; desc: string }

export function HelpTip({
  text,
  defs,
  label = 'Ayuda',
  size = 14,
}: {
  text?: ReactNode
  /** Mini-glosario opcional (término → definición), para explicar opciones. */
  defs?: readonly HelpDef[]
  label?: string
  size?: number
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  // Cerrar al tocar fuera, o al hacer scroll/resize (la posición fixed dejaría de cuadrar).
  useEffect(() => {
    if (!open) return
    function onDoc(e: PointerEvent) {
      const t = e.target as Node
      if (triggerRef.current?.contains(t) || popRef.current?.contains(t)) return
      setOpen(false)
    }
    function onMove() { setOpen(false) }
    document.addEventListener('pointerdown', onDoc)
    window.addEventListener('resize', onMove)
    window.addEventListener('scroll', onMove, true)
    return () => {
      document.removeEventListener('pointerdown', onDoc)
      window.removeEventListener('resize', onMove)
      window.removeEventListener('scroll', onMove, true)
    }
  }, [open])

  // Posicionar tras render (mide la burbuja real): preferir arriba; si no cabe, abajo;
  // clamp horizontal y vertical al viewport con un margen.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current || !popRef.current) return
    const tr = triggerRef.current.getBoundingClientRect()
    const pr = popRef.current.getBoundingClientRect()
    const margin = 8, gap = 8
    const vw = window.innerWidth, vh = window.innerHeight
    let left = tr.left + tr.width / 2 - pr.width / 2
    left = Math.max(margin, Math.min(left, vw - pr.width - margin))
    let top = tr.top - pr.height - gap // arriba
    if (top < margin) top = Math.min(tr.bottom + gap, vh - pr.height - margin) // flip abajo
    top = Math.max(margin, top)
    setPos({ top, left })
  }, [open, text, defs])

  return (
    <span ref={triggerRef} style={{ display: 'inline-flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
      <span
        role="button"
        tabIndex={0}
        aria-label={label}
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); setOpen((o) => !o) }}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o) } }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
          color: 'var(--text-tertiary)', opacity: 0.85,
        }}
      >
        <Info size={size} />
      </span>
      {open && createPortal(
        <div
          ref={popRef}
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'fixed',
            top: pos ? pos.top : -9999, left: pos ? pos.left : -9999,
            visibility: pos ? 'visible' : 'hidden',
            zIndex: 3000, width: 'max-content', maxWidth: 300, textAlign: 'left',
            padding: '10px 12px', borderRadius: 'var(--pos-radius-md, 8px)',
            // Colores FIJOS de alto contraste (legible en claro Y oscuro).
            background: '#1f2430', color: '#ffffff', border: '1px solid rgba(255,255,255,.14)',
            fontSize: '12px', fontWeight: 400, lineHeight: 1.4,
            boxShadow: '0 8px 24px rgba(0,0,0,.4)', whiteSpace: 'normal',
          }}
        >
          {text != null && <div>{text}</div>}
          {defs && defs.length > 0 && (
            <div style={{ margin: text != null ? '8px 0 0' : 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {defs.map((d, i) => (
                <div key={i}>
                  <span style={{ fontWeight: 600 }}>{d.term}:</span>{' '}
                  <span style={{ opacity: 0.85 }}>{d.desc}</span>
                </div>
              ))}
            </div>
          )}
        </div>,
        document.body,
      )}
    </span>
  )
}
